import React from 'react'
import {useSelector} from 'react-redux'
import {listNotificationSettingsByResident, createNotificationSetting, updateNotificationSetting} from '../../lib/queries'
import {
  NOTIFICATION_CATEGORY_RXO_UNREAD_RESIDENT_MESSAGE,
  NOTIFICATION_CATEGORY_RXO_DELAYED_CHAT_RESPONSE,
  NOTIFICATION_CATEGORY_RXO_24HOUR_DELAYED_CHAT_RESPONSE,
  NOTIFICATION_MEDIUM_EMAIL,
  NOTIFICATION_CATEGORY_RXO_STAFF_AMENITY_RESERVATION_REQUEST,
  NOTIFICATION_CATEGORY_RXO_DELAYED_ONBOARDING_ITEMS_TO_REVIEW,
  NOTIFICATION_CATEGORY_RXO_NEW_ONBOARDING_ITEM_TO_REVIEW,
} from '../../constants/ModelConstants'

// Configuration for all notification types - add new notifications here
const NOTIFICATION_TYPES = {
  NotifyEveryMessage: NOTIFICATION_CATEGORY_RXO_UNREAD_RESIDENT_MESSAGE,
  NotifyWithDelay: NOTIFICATION_CATEGORY_RXO_DELAYED_CHAT_RESPONSE,
  NotifyWith24HourDelay: NOTIFICATION_CATEGORY_RXO_24HOUR_DELAYED_CHAT_RESPONSE,
  NotifyAmenityReservationRequests: NOTIFICATION_CATEGORY_RXO_STAFF_AMENITY_RESERVATION_REQUEST,
  NotifyOnboardingItemsToReviewWithDelay: NOTIFICATION_CATEGORY_RXO_DELAYED_ONBOARDING_ITEMS_TO_REVIEW,
  NotifyNewOnboardingItemToReview: NOTIFICATION_CATEGORY_RXO_NEW_ONBOARDING_ITEM_TO_REVIEW,
}

const NOTIFICATION_KEYS = Object.keys(NOTIFICATION_TYPES)

const createInitialState = () =>
  NOTIFICATION_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: {original: null, staged: null, fullSetting: null},
    }),
    {},
  )

const useNotificationSettings = () => {
  const authedStaffModel = useSelector(state => state.Staff.authedStaffModel)
  const permissionsObject = useSelector(state => state.GroupPermissions.permissionsObject)

  const [notificationState, setNotificationState] = React.useState(createInitialState)
  const [isLoadingNotificationSettings, setIsLoadingNotificationSettings] = React.useState(true)
  const [loadNotificationSettingsError, setLoadNotificationSettingsError] = React.useState(null)

  React.useEffect(() => {
    if (!authedStaffModel) return
    setIsLoadingNotificationSettings(true)
    listNotificationSettingsByResident(authedStaffModel.id)
      .then(notificationSettings => {
        refreshNotificationSettingsLocalStateVariables(notificationSettings)
      })
      .catch(err => {
        console.error('Error fetching notification settings:', err)
        setLoadNotificationSettingsError(`Failed to load notification settings. Error: ${err}`)
        setIsLoadingNotificationSettings(false)
      })
  }, [authedStaffModel])

  const refreshNotificationSettingsLocalStateVariables = notificationSettings => {
    const newState = {}
    for (const key of NOTIFICATION_KEYS) {
      const category = NOTIFICATION_TYPES[key]
      const setting = notificationSettings.find(s => s.category === category)
      const value = setting ? !setting.isUnsubscribed : false
      newState[key] = {original: value, staged: value, fullSetting: setting || null}
    }
    setNotificationState(newState)
    setIsLoadingNotificationSettings(false)
  }

  const cancelNotificationPreferences = () => {
    setNotificationState(prev => {
      const newState = {}
      for (const key of NOTIFICATION_KEYS) {
        newState[key] = {...prev[key], staged: prev[key].original}
      }
      return newState
    })
  }

  const createOrUpdateNotificationSettingAsNeeded = async key => {
    const {original, staged, fullSetting} = notificationState[key]
    if (staged === original) return

    if (fullSetting) {
      await updateNotificationSettingWrapper(fullSetting.id, staged)
    } else {
      await createNotificationSettingWrapper(NOTIFICATION_TYPES[key], staged)
    }
  }

  const updateNotificationSettings = async () => {
    for (const key of NOTIFICATION_KEYS) {
      await createOrUpdateNotificationSettingAsNeeded(key)
    }

    setIsLoadingNotificationSettings(true)
    try {
      const updatedNotificationSettings = await listNotificationSettingsByResident(authedStaffModel.id)
      refreshNotificationSettingsLocalStateVariables(updatedNotificationSettings)
    } catch (err) {
      console.error('Error fetching notification settings:', err)
      setLoadNotificationSettingsError(`Failed to load notification settings. Error: ${err}`)
      setIsLoadingNotificationSettings(false)
    }
  }

  const updateNotificationSettingWrapper = async (notificationSettingId, stagedSetting) => {
    const updateNotificationSettingInput = {
      id: notificationSettingId,
      isUnsubscribed: !stagedSetting,
    }
    await updateNotificationSetting(updateNotificationSettingInput)
  }

  const createNotificationSettingWrapper = async (category, stagedSetting) => {
    const createNotificationSettingInput = {
      category: category,
      medium: NOTIFICATION_MEDIUM_EMAIL,
      residentId: authedStaffModel.id,
      isUnsubscribed: !stagedSetting,
    }
    await createNotificationSetting(createNotificationSettingInput)
  }

  const createStagedSetter = key => value => {
    setNotificationState(prev => ({
      ...prev,
      [key]: {...prev[key], staged: value},
    }))
  }

  const userHasMadeNotificationChanges = NOTIFICATION_KEYS.some(
    key => notificationState[key].staged !== notificationState[key].original,
  )

  // Build backwards-compatible return object
  return {
    // Original values
    originalNotifyEveryMessage: notificationState.NotifyEveryMessage.original,
    originalNotifyWithDelay: notificationState.NotifyWithDelay.original,
    originalNotifyWith24HourDelay: notificationState.NotifyWith24HourDelay.original,
    originalNotifyAmenityReservationRequests: notificationState.NotifyAmenityReservationRequests.original,
    originalNotifyOnboardingItemsToReviewWithDelay: notificationState.NotifyOnboardingItemsToReviewWithDelay.original,
    originalNotifyNewOnboardingItemToReview: notificationState.NotifyNewOnboardingItemToReview.original,
    // Staged values
    stagedNotifyEveryMessage: notificationState.NotifyEveryMessage.staged,
    stagedNotifyWithDelay: notificationState.NotifyWithDelay.staged,
    stagedNotifyWith24HourDelay: notificationState.NotifyWith24HourDelay.staged,
    stagedNotifyAmenityReservationRequests: notificationState.NotifyAmenityReservationRequests.staged,
    stagedNotifyOnboardingItemsToReviewWithDelay: notificationState.NotifyOnboardingItemsToReviewWithDelay.staged,
    stagedNotifyNewOnboardingItemToReview: notificationState.NotifyNewOnboardingItemToReview.staged,
    // Setters
    setStagedNotifyEveryMessage: createStagedSetter('NotifyEveryMessage'),
    setStagedNotifyWithDelay: createStagedSetter('NotifyWithDelay'),
    setStagedNotifyWith24HourDelay: createStagedSetter('NotifyWith24HourDelay'),
    setStagedNotifyAmenityReservationRequests: createStagedSetter('NotifyAmenityReservationRequests'),
    setStagedNotifyOnboardingItemsToReviewWithDelay: createStagedSetter('NotifyOnboardingItemsToReviewWithDelay'),
    setStagedNotifyNewOnboardingItemToReview: createStagedSetter('NotifyNewOnboardingItemToReview'),
    // Other
    isLoadingNotificationSettings,
    loadNotificationSettingsError,
    updateNotificationSettings,
    userHasMadeNotificationChanges,
    cancelNotificationPreferences,
    permissionsObject,
  }
}

export {useNotificationSettings}
